import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Camera, CameraOff, PackagePlus, ScanLine, Search, X, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Field, Input, Select, DateInput, Spinner, inputCls } from "../../components/ui-kit";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail);

export default function Magazzino() {
  const { ctx } = useOutletContext();
  const sites = ctx.sites || [];
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [pendingBarcode, setPendingBarcode] = useState(null); // barcode in attesa di associazione
  const [assignIngredientId, setAssignIngredientId] = useState("");
  const [lotDraft, setLotDraft] = useState(null); // { barcode, ingredient, supplier_id }
  const [saving, setSaving] = useState(false);
  const [recentMappings, setRecentMappings] = useState([]);
  const manualInputRef = useRef(null);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    api.get("/app/catalog/ingredients").then(({ data }) => setIngredients((data.ingredients || []).filter((item) => !item.source_recipe_group_id)));
    api.get("/app/catalog/suppliers").then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => setSuppliers([]));
    api.get("/app/magazzino/barcode-map").then(({ data }) => setRecentMappings(data.mappings || [])).catch(() => {});
  }, []);

  useEffect(() => { manualInputRef.current?.focus(); }, [pendingBarcode, lotDraft]);

  const ingredientName = (id) => ingredients.find((i) => i.id === id)?.name || id;

  const stopCamera = () => {
    readerRef.current?.stopContinuousDecode?.();
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    setScanning(false);
  };

  const startCamera = async () => {
    setCameraError("");
    setScanning(true);
    try {
      readerRef.current = new BrowserMultiFormatReader();
      await readerRef.current.decodeFromVideoDevice(undefined, videoRef.current, (result, error) => {
        if (result) {
          handleScanned(result.getText());
          stopCamera();
        }
      });
    } catch (e) {
      setCameraError("Fotocamera non disponibile o permesso negato. Usa il campo manuale qui sotto (funziona anche con uno scanner USB/Bluetooth).");
      setScanning(false);
    }
  };

  useEffect(() => () => stopCamera(), []);

  const handleScanned = async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;
    try {
      const { data } = await api.get(`/app/magazzino/barcode/${encodeURIComponent(trimmed)}`);
      setLotDraft({ barcode: trimmed, ingredient: data.ingredient, supplier_id: data.mapping.supplier_id || "",
        supplier_lot: "", received_date: "", expiry_date: "", quantity: "", unit: data.ingredient.unit || "kg" });
      setPendingBarcode(null);
      toast.success(`Riconosciuto: ${data.ingredient.name}`);
    } catch (e) {
      if (e.response?.status === 404) {
        setPendingBarcode(trimmed);
        setAssignIngredientId("");
      } else {
        toast.error(err(e));
      }
    }
    setManualCode("");
  };

  const submitManual = (event) => {
    event.preventDefault();
    handleScanned(manualCode);
  };

  const confirmAssignment = async () => {
    if (!assignIngredientId) { toast.error("Seleziona a quale materia prima corrisponde questo barcode"); return; }
    try {
      await api.post("/app/magazzino/barcode-map", { barcode: pendingBarcode, ingredient_id: assignIngredientId });
      const ingredient = ingredients.find((i) => i.id === assignIngredientId);
      toast.success("Barcode associato: la prossima scansione lo riconoscerà automaticamente");
      setLotDraft({ barcode: pendingBarcode, ingredient, supplier_id: "", supplier_lot: "", received_date: "", expiry_date: "", quantity: "", unit: ingredient?.unit || "kg" });
      setPendingBarcode(null);
      setRecentMappings((current) => [{ id: "tmp", barcode: pendingBarcode, ingredient_id: assignIngredientId, created_at: new Date().toISOString() }, ...current]);
    } catch (e) { toast.error(err(e)); }
  };

  const submitLot = async () => {
    if (!siteId) { toast.error("Seleziona la sede di ricevimento"); return; }
    if (!lotDraft.supplier_id) { toast.error("Seleziona il fornitore"); return; }
    setSaving(true);
    try {
      await api.post("/app/catalog/raw-lots", {
        ingredient_id: lotDraft.ingredient.id, site_id: siteId, supplier_id: lotDraft.supplier_id,
        supplier_lot: lotDraft.supplier_lot.trim().toUpperCase(), received_date: lotDraft.received_date,
        expiry_date: lotDraft.expiry_date, quantity: lotDraft.quantity === "" ? null : Number(lotDraft.quantity),
        unit: lotDraft.unit,
      });
      if (lotDraft.supplier_id) await api.post("/app/magazzino/barcode-map", { barcode: lotDraft.barcode, ingredient_id: lotDraft.ingredient.id, supplier_id: lotDraft.supplier_id });
      toast.success("Lotto caricato in magazzino");
      setLotDraft(null);
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="magazzino-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Magazzino</p>
        <div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold text-navy">Carico rapido da scansione</h1><AcademyTitleBadge /></div>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">Scansiona il barcode già stampato sulla confezione del fornitore (o digitalo con uno scanner USB/Bluetooth) per caricare velocemente il lotto in Materie prime, senza cercarlo a mano. Riguarda solo le materie prime in ingresso, non le etichette di produzione.</p>
      </div>

      {!lotDraft && !pendingBarcode && (
        <Card className="p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <Field label="Sede di ricevimento"><Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</Select></Field>
            {!scanning
              ? <Button variant="primary" onClick={startCamera} data-testid="magazzino-start-camera"><Camera className="h-4 w-4" /> Scansiona con la fotocamera</Button>
              : <Button variant="outline" onClick={stopCamera}><CameraOff className="h-4 w-4" /> Interrompi</Button>}
          </div>

          {scanning && (
            <div className="mt-4 overflow-hidden rounded-xl border-2 border-navy bg-black">
              <video ref={videoRef} className="mx-auto max-h-72 w-full object-contain" muted playsInline />
            </div>
          )}
          {cameraError && <p className="mt-3 text-sm text-amber-700">{cameraError}</p>}

          <form onSubmit={submitManual} className="mt-5 border-t border-hairline pt-5">
            <Field label="Oppure digita/scansiona qui con uno scanner USB o Bluetooth" hint="Uno scanner fisico funziona come una tastiera: punta il codice e il numero comparirà qui da solo.">
              <div className="flex gap-2">
                <input ref={manualInputRef} className={inputCls} value={manualCode} onChange={(e) => setManualCode(e.target.value)} placeholder="Inquadra o digita il codice" data-testid="magazzino-manual-code" />
                <Button type="submit" variant="outline" className="shrink-0 whitespace-nowrap"><Search className="h-4 w-4" /> Cerca</Button>
              </div>
            </Field>
          </form>

          {recentMappings.length > 0 && (
            <div className="mt-6">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Barcode già associati</div>
              <div className="space-y-1.5">
                {recentMappings.slice(0, 8).map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono text-xs text-slate-500 truncate">{m.barcode}</span>
                    <span className="font-medium text-navy">{ingredientName(m.ingredient_id)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {pendingBarcode && (
        <Card className="p-5 sm:p-6" data-testid="magazzino-assign">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-navy">Barcode non ancora conosciuto</h2>
              <p className="mt-1 text-sm text-slate-500">Codice: <span className="font-mono">{pendingBarcode}</span>. A quale materia prima corrisponde? Le prossime volte verrà riconosciuto da solo.</p>
            </div>
            <button onClick={() => setPendingBarcode(null)} className="shrink-0 rounded-lg p-2 text-slate-400 hover:text-navy" aria-label="Annulla"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-4"><Field label="Materia prima"><Select value={assignIngredientId} onChange={(e) => setAssignIngredientId(e.target.value)} data-testid="magazzino-assign-select"><option value="">Seleziona...</option>{ingredients.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</Select></Field></div>
          <div className="mt-4"><Button variant="success" onClick={confirmAssignment} data-testid="magazzino-assign-confirm"><CheckCircle2 className="h-4 w-4" /> Associa e continua</Button></div>
        </Card>
      )}

      {lotDraft && (
        <Card className="p-5 sm:p-6" data-testid="magazzino-lot-form">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-navy">{lotDraft.ingredient.name}</h2>
              <p className="mt-1 text-xs font-mono text-slate-400">Barcode: {lotDraft.barcode}</p>
            </div>
            <button onClick={() => setLotDraft(null)} className="shrink-0 rounded-lg p-2 text-slate-400 hover:text-navy" aria-label="Annulla"><X className="h-5 w-5" /></button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Fornitore *"><Select value={lotDraft.supplier_id} onChange={(e) => setLotDraft({ ...lotDraft, supplier_id: e.target.value })} data-testid="magazzino-lot-supplier"><option value="">Seleziona...</option>{suppliers.filter((s) => s.active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
            <Field label="Lotto del fornitore *"><Input value={lotDraft.supplier_lot} onChange={(e) => setLotDraft({ ...lotDraft, supplier_lot: e.target.value.toUpperCase() })} data-testid="magazzino-lot-supplier-lot" /></Field>
            <Field label="Data di acquisto *"><DateInput value={lotDraft.received_date} onChange={(e) => setLotDraft({ ...lotDraft, received_date: e.target.value })} valueMode="iso" /></Field>
            <Field label="Scadenza / TMC *"><DateInput value={lotDraft.expiry_date} onChange={(e) => setLotDraft({ ...lotDraft, expiry_date: e.target.value })} valueMode="iso" /></Field>
            <Field label="Quantità ricevuta"><div className="flex gap-2"><Input type="number" step="0.1" value={lotDraft.quantity} onChange={(e) => setLotDraft({ ...lotDraft, quantity: e.target.value })} /><Select className="w-24" value={lotDraft.unit} onChange={(e) => setLotDraft({ ...lotDraft, unit: e.target.value })}><option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="pz">pz</option></Select></div></Field>
          </div>
          <div className="mt-5"><Button variant="success" loading={saving} onClick={submitLot} data-testid="magazzino-lot-submit"><PackagePlus className="h-4 w-4" /> Carica in magazzino</Button></div>
        </Card>
      )}
    </div>
  );
}
